import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  model,
  output,
  signal,
  viewChild,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { WebcamImage, WebcamInitError, WebcamModule, WebcamUtil } from 'ngx-webcam';
import { Observable, Subject } from 'rxjs';
import { ToastService } from 'src/app/services/toast.service';

import { ClarityModule } from '@clr/angular';

import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import dayjs from 'dayjs';
import { GetByCallbackPipe } from 'src/app/pipes/get-by-callback.pipe';

@Component({
  selector: 'app-camera',
  templateUrl: './camera.component.html',
  styleUrls: ['./camera.component.scss'],
  imports: [ClarityModule, WebcamModule, MrTranslatePipe, GetByCallbackPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraComponent implements OnInit, OnDestroy, AfterViewInit {
  public readonly takenFoto = output<string>();
  public readonly takenVideo = output<File>();
  public readonly showWebcam = model.required<boolean>();
  public readonly allowVideo = input<boolean>(true);

  protected readonly webcamReady = signal(false);
  private deviceId: string;
  protected readonly multipleWebcamsAvailable = signal(false);
  protected readonly allowCameraSwitch = signal(true);
  protected readonly videoOptions = signal<MediaTrackConstraints>({
    width: { ideal: 1280 }, // or exact: 1280
    height: { ideal: 720 }, // or exact: 720
    facingMode: 'environment',
  });

  protected readonly recordingTime = signal(0); // seconds
  private recordingStartTime = 0;
  private recordingTimerId: NodeJS.Timeout;

  // webcam snapshot trigger
  private trigger: Subject<void> = new Subject<void>();
  protected get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  private nextWebcam: Subject<boolean | string> = new Subject<boolean | string>();
  protected get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }

  protected readonly resizeReady = signal(true);
  private readonly sizeRef = viewChild<ElementRef<HTMLDivElement>>('sizeRef');
  protected readonly sizePx = signal<{ width?: number; height?: number }>({});
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);

  protected readonly isVideoMode = signal(false);
  protected readonly isVideoRecording = signal(false);
  private mediaRecorder!: MediaRecorder;
  private recordedChunks: Blob[] = [];
  private stream!: MediaStream;

  async ngOnInit() {
    const mediaDevices = await WebcamUtil.getAvailableVideoInputs();
    if (mediaDevices?.length < 2) return;
    this.multipleWebcamsAvailable.set(true);

    const id = localStorage.getItem('camera_deviceID');
    if (id && id != this.deviceId && mediaDevices.some(x => x.deviceId == id))
      setTimeout(() => this.showNextWebcam(id));
  }

  async startRecording() {
    this.isVideoRecording.set(!this.isVideoRecording());

    if (!this.isVideoRecording()) {
      this.stopRecording();
      return;
    }
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: this.videoOptions(),
      audio: true,
    });

    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType: 'video/webm;codecs=vp8',
    });

    this.recordedChunks = [];

    this.mediaRecorder.ondataavailable = event => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };

    this.recordingStartTime = Date.now();
    this.recordingTime.set(0);

    this.recordingTimerId = setInterval(() => {
      const seconds = Math.floor((Date.now() - this.recordingStartTime) / 1000);
      this.recordingTime.set(seconds);
    }, 500);

    this.mediaRecorder.start();
  }

  stopRecording() {
    this.mediaRecorder.stop();
    this.stream.getTracks().forEach(t => t.stop());

    clearInterval(this.recordingTimerId);
    this.recordingTimerId = null;

    setTimeout(() => {
      const date = dayjs().format('MM_DD_HHmmss');

      const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
      const file = new File([blob], date + '.webm', {
        type: blob.type,
        lastModified: Date.now(),
      });

      this.takenVideo.emit(file);

      setTimeout(() => {
        this.showWebcam.set(false);
      }, 500);
    }, 10);
  }

  protected readonly recordingTimeFormatted = (n: number): string => {
    const t = n;
    const m = Math.floor(t / 60)
      .toString()
      .padStart(2, '0');
    const s = (t % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.resizeCamera();
    });
    screen.orientation.onchange = _ => {
      this.resizeReady.set(false);
      this.resizeCamera();
      setTimeout(() => {
        this.resizeReady.set(true);
      });
    };
  }

  private resizeCamera() {
    if (this.sizeRef() && this.showWebcam()) {
      const width = this.sizeRef().nativeElement.offsetWidth - 2;
      const height = this.sizeRef().nativeElement.parentElement.offsetHeight - 10;
      let resolition = {
        width: { ideal: 1280 }, // or exact: 1280
        height: { ideal: 720 }, // or exact: 720
        facingMode: 'environment',
      }; // Horizonal
      let faktor = 1080 / 1280;
      if (width < height) {
        resolition = {
          width: { ideal: 720 }, // or exact: 1280
          height: { ideal: 1280 }, // or exact: 720
          facingMode: 'environment',
        };
      } // Vertical
      this.videoOptions.set(resolition);
      this.sizePx.set({
        height: resolition.height.ideal * faktor,
        width: resolition.width.ideal * faktor,
      });
    }
  }

  protected takePicture() {
    this.trigger.next();
  }

  protected handleImage(image: WebcamImage) {
    this.takenFoto.emit(image.imageAsDataUrl);
    this.showWebcam.set(false);
  }

  protected handleInitError(error: WebcamInitError): void {
    this.toastr.error(this.mrTranslate.transform(error.message));
    this.showWebcam.set(false);
  }

  protected showNextWebcam(directionOrDeviceId: boolean | string): void {
    this.nextWebcam.next(directionOrDeviceId);
  }

  protected saveDeviceID(deviceId: string) {
    if (!deviceId || !this.multipleWebcamsAvailable()) return;
    localStorage.setItem('camera_deviceID', deviceId);
    this.deviceId = deviceId;
  }

  ngOnDestroy() {
    this.stream?.getTracks().forEach(t => t.stop());
  }
}
